import React from "react";
import type { BraillerLoanManagementItem } from '../brailler.types';
import {SelectField, TextArea, TextField, Button} from "../../../../Components/Inputs";
import {
    BRAILLER_LOAN_STATUS_DISPLAY_OPTIONS,
    BRAILLER_REPAIR_STATUS_DISPLAY_OPTIONS
} from "./braillerHistoryConstants";
import { getReplacementStatusLabel, canCancelLoan, getCancelWarningMessage } from "./braillerLoanManagementUtility";
import { BRAILLER_REPLACEMENT_STATUS, BRAILLER_REPLACEMENT_STATUS_LIST, REPAIR_STATUS } from "../braillerConstants";
import {HtmlDisplay} from "../../../../Components/Display";
import {PATRON_SUBTYPE_LIST} from "../../../../Components/Authorize/roleConstants";

export const BraillerLoanManagementEditFormView = ({
    itemForDialog,
    setItemForDialog,
    onOpenReplacementDialog,
    onCancelLoan
}: {
    itemForDialog: BraillerLoanManagementItem;
    setItemForDialog: (item: BraillerLoanManagementItem) => void;
    onOpenReplacementDialog?: (item: BraillerLoanManagementItem) => void;
    onCancelLoan?: (loanId: string, notes: string | null) => void;
}) => {
    const isLoan = itemForDialog.type === 'Loan';
    const isRepair = itemForDialog.type === 'Repair';
    const loanHasActiveRepair = isLoan && itemForDialog.braillerRepairId;

    const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (isLoan) {
            setItemForDialog({...itemForDialog, loanStatus: parseInt(event.target.value)});
        } else {
            setItemForDialog({...itemForDialog, repairStatus: parseInt(event.target.value)});
        }
    };

    const handleRepairStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setItemForDialog({...itemForDialog, repairStatus: parseInt(event.target.value)});
    };

    const handleReplacementPreferenceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setItemForDialog({...itemForDialog, replacementPreference: parseInt(event.target.value)});
    };

    const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setItemForDialog({...itemForDialog, notes: event.target.value});
    };

    const handleIssueChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setItemForDialog({...itemForDialog, issue: event.target.value});
    };

    const handleResolutionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setItemForDialog({...itemForDialog, resolution: event.target.value});
    };

    const handleSerialNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setItemForDialog({...itemForDialog, serialNumber: event.target.value});
    };

    const handleInventoryNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setItemForDialog({...itemForDialog, inventoryNotes: event.target.value});
    };

    const handleCancelClick = () => {
        if (!onCancelLoan) return;
        
        const warningMessage = getCancelWarningMessage(itemForDialog.loanStatus);
        let confirmMessage = 'Are you sure you want to cancel this loan?';
        
        if (warningMessage) {
            confirmMessage = `Warning: ${warningMessage}\n\nAre you sure you want to cancel this loan? The brailler will be returned to inventory and may be assigned to a waitlisted item.`;
        } else {
            confirmMessage = 'Are you sure you want to cancel this loan? The brailler will be returned to inventory and may be assigned to a waitlisted item.';
        }
        
        if (confirm(confirmMessage)) {
            onCancelLoan(itemForDialog.braillerLoanId || '', itemForDialog.notes);
        }
    };

    const currentStatus = isLoan ? itemForDialog.loanStatus : itemForDialog.repairStatus;
    
    // Check if replacement is needed and not already created
    const needsReplacement = itemForDialog.replacementPreference && 
        itemForDialog.replacementPreference !== BRAILLER_REPLACEMENT_STATUS.NotNeeded.id;
    const canCreateReplacement = needsReplacement && 
        !itemForDialog.hasReplacementLoan && 
        (itemForDialog.repairStatus === REPAIR_STATUS.Reported.id || 
         itemForDialog.repairStatus === REPAIR_STATUS.Approved.id);

    // Can edit replacement preference only if repair is active (not terminal)
    const canEditReplacementPreference = 
        itemForDialog.repairStatus === REPAIR_STATUS.Reported.id || 
        itemForDialog.repairStatus === REPAIR_STATUS.Approved.id;

    // Can cancel loan if it's a loan type and not already returned or cancelled
    const showCancelButton = isLoan && onCancelLoan && canCancelLoan(itemForDialog.loanStatus);

    return <>
        <TextField
            label="Serial Number"
            name="serialNumber"
            onChange={handleSerialNumberChange}
            value={itemForDialog.serialNumber || ""}
            helpText="Change the serial number to swap to a different brailler"
        />

        {/* Info message when serial number change is not available due to active repair */}
        {isLoan && loanHasActiveRepair && !itemForDialog.dateReturned && (
            <div style={{ 
                marginBottom: '15px', 
                padding: '10px', 
                backgroundColor: '#fff3cd', 
                borderRadius: '4px', 
                border: '1px solid #ffc107',
                fontSize: '0.9em'
            }}>
                <strong>Note:</strong> Serial number changes are not available while an active repair exists. 
                Complete or cancel the repair first.
            </div>
        )}

        {
            itemForDialog.patron &&
            <>
                {
                    itemForDialog.patron.patronSubType &&
                    <HtmlDisplay
                        html={`Patron Role: ${PATRON_SUBTYPE_LIST.find((f) => f.value === itemForDialog.patron?.patronSubType)?.text || ``}`}
                        name={`patron-subtype-${itemForDialog.braillerLoanId}`}
                    />
                }
                {
                    itemForDialog.patron.district &&
                    <HtmlDisplay
                        html={`Patron District: ${itemForDialog.patron.district.districtName}`}
                        name={`patron-district-${itemForDialog.braillerLoanId}`}
                    />
                }
                {
                    itemForDialog.patron.organization &&
                    <HtmlDisplay
                        html={`Patron Organization: ${itemForDialog.patron.organization}`}
                        name={`patron-organization-${itemForDialog.braillerLoanId}`}
                    />
                }
            </>
        }
        {
            itemForDialog.issue &&
            <HtmlDisplay
                html={`Issue: ${itemForDialog.issue}`}
                name={`issue-${itemForDialog.braillerLoanId}`}
            />
        }

        {isLoan && (
            <>
                <SelectField
                    includeDefaultOption={false}
                    label="Loan Status"
                    name="loanStatus"
                    onChange={handleStatusChange}
                    options={BRAILLER_LOAN_STATUS_DISPLAY_OPTIONS}
                    value={currentStatus ?? undefined}
                />

                <TextArea
                    label="Loan Notes"
                    name="loanNotes"
                    onChange={handleNotesChange}
                    value={itemForDialog.notes || ""}
                    helpText="Add or update notes for this loan"
                />

                {showCancelButton && (
                    <div style={{ marginTop: '15px', marginBottom: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                        <div style={{ marginBottom: '10px', color: '#856404' }}>
                            <strong>Cancel Loan</strong>
                            <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
                                Cancelling this loan will return the brailler to inventory. If there are waitlisted items, the brailler may be automatically assigned to the next person in line.
                            </p>
                        </div>
                        <Button
                            label="Cancel Loan"
                            name="btnCancelLoan"
                            onClick={handleCancelClick}
                            style={{ backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                        />
                    </div>
                )}
            </>
        )}

        {loanHasActiveRepair && (
            <>
                <div className="form-section-divider"
                     style={{marginTop: '20px', marginBottom: '15px', borderTop: '2px solid #ccc', paddingTop: '15px'}}>
                    <h3 style={{marginTop: 0, marginBottom: '10px'}}>Active Repair Information</h3>
                </div>

                <SelectField
                    includeDefaultOption={false}
                    label="Repair Status"
                    name="repairStatus"
                    onChange={handleRepairStatusChange}
                    options={BRAILLER_REPAIR_STATUS_DISPLAY_OPTIONS}
                    value={itemForDialog.repairStatus ?? undefined}
                />

                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    {canEditReplacementPreference ? (
                        <SelectField
                            includeDefaultOption={false}
                            label="Replacement Preference"
                            name="replacementPreference"
                            onChange={handleReplacementPreferenceChange}
                            options={BRAILLER_REPLACEMENT_STATUS_LIST}
                            value={itemForDialog.replacementPreference || BRAILLER_REPLACEMENT_STATUS.NotNeeded.id}
                            helpText="Set whether a replacement brailler should be sent"
                        />
                    ) : (
                        <div>
                            <strong>Replacement Preference:</strong> {getReplacementStatusLabel(itemForDialog.replacementPreference)}
                        </div>
                    )}
                    
                    {itemForDialog.hasReplacementLoan ? (
                        <div style={{ color: 'green', marginTop: '5px' }}>
                            Replacement loan has been created
                        </div>
                    ) : canCreateReplacement && onOpenReplacementDialog ? (
                        <div style={{ marginTop: '10px' }}>
                            <Button
                                isPrimary
                                label="Create Replacement Loan"
                                name="btnCreateReplacement"
                                onClick={() => onOpenReplacementDialog(itemForDialog)}
                            />
                        </div>
                    ) : null}
                </div>

                <TextArea
                    label="Repair Issue"
                    name="repairIssue"
                    value={itemForDialog.issue || ""}
                    onChange={handleIssueChange}
                    helpText="The original issue reported"
                />

                <TextArea
                    label="Repair Resolution"
                    name="repairResolution"
                    value={itemForDialog.resolution || ""}
                    onChange={handleResolutionChange}
                    helpText="Resolution details or updates for this repair"
                />
            </>
        )}

        {isRepair && (
            <>
                <SelectField
                    includeDefaultOption={false}
                    label="Repair Status"
                    name="repairStatus"
                    onChange={handleStatusChange}
                    options={BRAILLER_REPAIR_STATUS_DISPLAY_OPTIONS}
                    value={currentStatus ?? undefined}
                />

                <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    {canEditReplacementPreference ? (
                        <SelectField
                            includeDefaultOption={false}
                            label="Replacement Preference"
                            name="replacementPreference"
                            onChange={handleReplacementPreferenceChange}
                            options={BRAILLER_REPLACEMENT_STATUS_LIST}
                            value={itemForDialog.replacementPreference || BRAILLER_REPLACEMENT_STATUS.NotNeeded.id}
                            helpText="Set whether a replacement brailler should be sent"
                        />
                    ) : (
                        <div>
                            <strong>Replacement Preference:</strong> {getReplacementStatusLabel(itemForDialog.replacementPreference)}
                        </div>
                    )}
                    
                    {itemForDialog.hasReplacementLoan ? (
                        <div style={{ color: 'green', marginTop: '5px' }}>
                            Replacement loan has been created
                        </div>
                    ) : canCreateReplacement && onOpenReplacementDialog ? (
                        <div style={{ marginTop: '10px' }}>
                            <Button
                                isPrimary
                                label="Create Replacement Loan"
                                name="btnCreateReplacement"
                                onClick={() => onOpenReplacementDialog(itemForDialog)}
                            />
                        </div>
                    ) : null}
                </div>

                <TextArea
                    label="Repair Issue"
                    name="repairIssue"
                    value={itemForDialog.issue || ""}
                    onChange={handleIssueChange}
                    helpText="The original issue reported - can be updated to add more details"
                />

                <TextArea
                    label="Repair Resolution"
                    name="repairResolution"
                    onChange={handleNotesChange}
                    value={itemForDialog.notes || ""}
                    helpText="Add resolution details or updates for this repair"
                />
            </>
        )}

        <div className="form-section-divider"
             style={{marginTop: '20px', marginBottom: '15px', borderTop: '1px solid #ddd', paddingTop: '15px'}}>
            <h4 style={{marginTop: 0, marginBottom: '10px'}}>Inventory Information</h4>
        </div>

        <TextArea
            label="Inventory Notes"
            name="inventoryNotes"
            value={itemForDialog.inventoryNotes || ""}
            onChange={handleInventoryNotesChange}
            helpText="General notes about the physical brailler item (affects all loans/repairs of this unit)"
        />
    </>;
};
